import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAuthToken, setUnauthorizedHandler } from '../../lib/api';

/**
 * Auth token drží v paměti (v contextu + v api modulu).
 * Refresh token, pokud je, ukládá se do HttpOnly cookie serverem — client ho nevidí.
 * Tím chráníme před XSS a současně zachováváme "remember me" přes refresh flow.
 */

export interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_admin: boolean;
  can_zapis_kd: boolean;
  created_at: string;
  /** Vzhled per účet (Nastavení účtu → Vzhled) */
  ui_theme?: 'dark' | 'light';
  ui_doc_white?: boolean;
}

interface LoginResponse {
  access_token: string;
  token_type?: string;
  user?: User;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Obnoví vlastní deaktivovaný účet a rovnou přihlásí. */
  reactivate: (email: string, password: string) => Promise<void>;
  /** Po úpravě v Nastavení účtu — přepíše uloženého uživatele. */
  updateUser: (user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'sd_auth';

type StoredAuth = { token: string; user: User | null };

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

function saveStored(auth: StoredAuth | null): void {
  try {
    if (auth) localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* quota / private mode — ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAuthToken(null);
    saveStored(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
  }, [logout]);

  useEffect(() => {
    const stored = loadStored();
    if (stored?.token) {
      setToken(stored.token);
      setUser(stored.user);
      setAuthToken(stored.token);
    }
    setIsLoading(false);
  }, []);

  const applyLogin = useCallback((res: LoginResponse) => {
    setToken(res.access_token);
    setAuthToken(res.access_token);
    const nextUser = res.user ?? null;
    setUser(nextUser);
    saveStored({ token: res.access_token, user: nextUser });
  }, []);

  const updateUser = useCallback((nextUser: User) => {
    setUser(nextUser);
    const stored = loadStored();
    if (stored?.token) saveStored({ token: stored.token, user: nextUser });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const form = new URLSearchParams();
    form.set('username', email);
    form.set('password', password);
    const res = await api.post<LoginResponse>('/auth/token', form.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    applyLogin(res);
  }, [applyLogin]);

  const reactivate = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/reactivate', { email, password });
    applyLogin(res);
  }, [applyLogin]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: !!token,
      isLoading,
      login,
      reactivate,
      updateUser,
      logout,
    }),
    [user, token, isLoading, login, reactivate, updateUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
